import {
  Button,
  makeStyles,
  TextField,
  Box,
  CircularProgress
} from "@material-ui/core";
import React, { useState, useEffect } from "react";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import useToggle from "../../../state/useToggle";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2)
  }
}));

export default function TransUnionCredsModal({ partner, saveAttribute }) {
  const classes = useStyles();
  const toggle = useToggle();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    subcode: "",
    password: "",
    name: ""
  });

  useEffect(() => {
    if (toggle.on && partner) {
      setFormData({
        subcode: partner.transunionFullCode || "",
        password: partner.transunionPassword || "",
        name: partner.transunionName || ""
      });
      setIsSubmitting(false);
    }
  }, [toggle.on, partner]);

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      await saveAttribute({
        transunionFullCode: formData.subcode.trim() || null,
        transunionPassword: formData.password.trim() || null,
        transunionName: formData.name.trim() || null
      });

      toggle.toggle();
    } catch {
      // Error toast already shown by saveAttribute
    } finally {
      setIsSubmitting(false);
    }
  };

  const subcodeValid = /^\d{4}[A-Za-z]\d{7}$/.test(formData.subcode.trim());
  const passwordValid = /^[A-Za-z0-9]{4}$/.test(formData.password.trim());
  const isFormValid = subcodeValid && passwordValid && formData.name.trim();

  const hasCredentials = Boolean(partner?.transunionFullCode);

  return (
    <>
      <Button variant="contained" color="secondary" onClick={toggle.toggle}>
        {hasCredentials ? "Update TransUnion Credentials" : "Enable TransUnion"}
      </Button>

      <ConfirmationDialog
        title={
          hasCredentials ? "Update TransUnion Credentials" : "Enable TransUnion"
        }
        open={toggle.on}
        onCancel={toggle.toggle}
        onConfirm={handleSave}
        text="Set the TransUnion subcode, password, and name for this partner."
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: isSubmitting ? <CircularProgress size={20} /> : "Save",
          disabled: !isFormValid || isSubmitting
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box className={classes.container}>
          <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
            <TextField
              label="Subcode"
              value={formData.subcode}
              onChange={(e) =>
                setFormData({ ...formData, subcode: e.target.value })
              }
              placeholder="e.g. 1302R4215671"
              error={formData.subcode.trim() !== "" && !subcodeValid}
              helperText={
                formData.subcode.trim() !== "" && !subcodeValid
                  ? "Format: 4 digits + letter + 7 digits (e.g. 1302R4215671)"
                  : "TransUnion full code / subcode"
              }
              required
              fullWidth
            />

            <TextField
              label="Password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              placeholder="e.g. R7W8"
              error={formData.password.trim() !== "" && !passwordValid}
              helperText="TransUnion password (e.g. R7W8)"
              required
              fullWidth
            />

            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g. BoomPay/Partner Name"
              helperText="TransUnion name identifier"
              required
              fullWidth
            />
          </Box>
        </Box>
      </ConfirmationDialog>
    </>
  );
}
